/**
 * OTA Configuration for AWS Server Integration
 * Environment-based configuration for Capgo OTA with custom AWS backend
 */

import type { OTAChannel } from './types'

// Environment-based configuration - ALWAYS use absolute URLs
// Using HTTPS with Let's Encrypt certificate and Nginx proxy
export const OTA_BASE = 'https://ota.test.deliverider.com' // Production HTTPS OTA server URL
export const OTA_CHANNEL = (process.env.REACT_APP_OTA_CHANNEL as OTAChannel) || 'production'
export const OTA_COOLDOWN_MIN = Number(process.env.REACT_APP_OTA_COOLDOWN_MIN || 60) // minutes between resume checks (reduced from 120 to 60)

// Runtime configuration validation
export function validateOTAConfig(): void {
  // OTA_BASE is a hardcoded constant, not env-driven, so this only ever fires if a
  // future edit accidentally reintroduces http:// — worth failing loudly rather than
  // silently downloading and applying bundles over an unencrypted connection.
  if (!OTA_BASE.startsWith('https://')) {
    throw new Error(`[OTA] OTA_BASE must use HTTPS, got: ${OTA_BASE}`)
  }

  const validChannels: OTAChannel[] = ['production', 'beta', 'canary', 'test', 'frisco-test', 'frisco-production']
  if (!validChannels.includes(OTA_CHANNEL)) {
    console.warn(`[OTA] Invalid OTA_CHANNEL: ${OTA_CHANNEL}. Valid options: ${validChannels.join(', ')}`)
  }

  if (OTA_COOLDOWN_MIN < 1 || OTA_COOLDOWN_MIN > 1440) {
    console.warn(`[OTA] OTA_COOLDOWN_MIN should be between 1-1440 minutes. Current: ${OTA_COOLDOWN_MIN}`)
  }
}

// Mirrors OTAUpdaterService's own debug gating (updater.ts) — these are free
// functions outside that class, so there's no `this.log()` to reuse, but the same
// reasoning applies: a shared/debug tablet's logcat is readable via adb without
// root, and this dumps the OTA server URL on every check.
const OTA_DEBUG = process.env.NODE_ENV === 'development'

// Helper functions for absolute URL construction
export function buildManifestUrl(channel: OTAChannel): string {
  const url = new URL(`/apps/com.deliverider.seller/channels/${channel}/latest`, OTA_BASE).href
  if (OTA_DEBUG) console.log('[OTA] buildManifestUrl =', url)
  return url
}

export function buildAbsoluteUrl(relativePath: string): string {
  const url = new URL(relativePath, OTA_BASE).href
  if (OTA_DEBUG) console.log('[OTA] buildAbsoluteUrl =', { relativePath, absolute: url })
  return url
}

// A manifest response is attacker-influenced any time the connection is intercepted
// (MITM) or the OTA server is compromised — it can name any URL as the bundle to
// download and run. Restricting downloads to OTA_BASE's own origin means a forged
// manifest can at worst point back at the real server, not at an attacker's host.
export function isAllowedBundleUrl(url: string): boolean {
  try {
    return new URL(url).origin === new URL(OTA_BASE).origin
  } catch {
    return false
  }
}

// Local storage keys for OTA data persistence
export const STORAGE_KEYS = {
  LAST_CHECK_TIME: 'ota_last_check_time',
  LAST_ETAG: 'ota_last_etag',
  CURRENT_CHANNEL: 'ota_current_channel',
  BUNDLE_VERSION: 'ota_bundle_version',
  FAILED_ATTEMPTS: 'ota_failed_attempts',
  // Bundle id staged via CapacitorUpdater.next(), tracked so a launch can promote it.
  //
  // next() only writes capgo's NEXT_VERSION pref. Capgo promotes it from
  // installNext(), which it calls in appMovedToBackground() — NOT at process start,
  // where it simply loads the current bundle. So a process that dies without a
  // graceful background (crash, force-stop, OS kill) leaves the staged bundle
  // un-promoted indefinitely. Recording the id lets us promote it deterministically.
  // (This is capgo's model generally; autoUpdate:false only disables its own polling.)
  PENDING_BUNDLE_ID: 'ota_pending_bundle_id',
  // The human version ("1.0.0+97") of the staged bundle. Stored separately because
  // PENDING_BUNDLE_ID holds capgo's internal id, which is a random 10-char string with
  // no version in it — comparing builds against that always yields 0.
  PENDING_BUNDLE_VERSION: 'ota_pending_bundle_version',
} as const
